import React from 'react';
import type {GetServerSideProps, NextPage} from 'next';
import {Content, ContentProps} from 'COMPONENTS/content/Content';
import {Components} from 'TYPES/types';
import {MetaHead} from 'COMPONENTS/content/meta/MetaHead';
import ErrorPage from 'COMPONENTS/pages-components/error-page/ErrorPage';
import Footer from 'COMPONENTS/pages-components/common/footer/Footer';
import {Sprite} from 'COMPONENTS/pages-components/common/Icon/Sprite';
import Header from 'COMPONENTS/pages-components/common/header/Header';
import {Cookies} from 'COMPONENTS/pages-components/common/cookies/Cookies';

type Params = {
  lang?: string | string[];
  version?: string | string[];
}

type Props = {
  data: ContentProps;
  statusCode?: string | number;
} & Components;

const asSingleParam = (value?: string | string[]) => {
  if (!value) {
    return '';
  }

  return Array.isArray(value) ? value[0] || '' : value;
};

export const getServerSideProps: GetServerSideProps<Props, Params> = async ({
  params
}) => {
  const baseUrl = process.env.NEXT_PUBLIC_API || '/api-back/api';
  const lang = asSingleParam(params?.lang);
  const version = asSingleParam(params?.version);

  if (!lang || !version) {
    return {notFound: true};
  }

  const privacyVersionUrl = `${baseUrl}/${lang}/privacy/version/${version}`;

  try {
    const [
      componentRes,
      dataRes
    ] = await Promise.all([
      fetch(`${baseUrl}/components`),
      fetch(privacyVersionUrl)
    ]);

    if (dataRes.status === 404) {
      return {notFound: true};
    }

    if (!componentRes.ok || !dataRes.ok) {
      throw new Error(`Privacy version request failed with status ${dataRes.status}`);
    }

    const data = await dataRes.json() as ContentProps;
    const components = await componentRes.json() as Components;

    return {
      props: {
        data,
        ...components
      }
    };
  } catch (e) {
    return {notFound: true};
  }
};

const PrivacyVersionPage: NextPage<Props> = ({data, header, ...props}) => {
  if (props.statusCode || !data || !data.items) {
    return <ErrorPage statusCode={props.statusCode || 404}/>;
  }

  return <>
    <MetaHead data={data.meta}/>
    <Cookies/>
    <Header {...header}/>
    <Content {...data}/>
    <Footer {...header.ftr}/>
    <Sprite/>
  </>;
};

export default PrivacyVersionPage;
